import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, MapPin, Tag } from 'lucide-react';
import PageBanner from '../components/PageBanner';
import Footer from '../components/Footer';
import { getEvents } from '../lib/api';
import { priceSummary, fmtTimeRange, parseDay, isPast } from '../lib/events';
import { IMAGES } from '../mock';

const MON = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

function EventCard({ e, past }) {
  const d = parseDay(e.date);
  return (
    <Link to={`/events/${e.id}`} data-testid={`event-card-${e.id}`}
      className={`group grid sm:grid-cols-[220px_1fr] bg-white rounded-sm overflow-hidden shadow-[0_6px_30px_rgba(0,0,0,0.05)] hover:shadow-[0_14px_44px_rgba(0,0,0,0.12)] transition-shadow duration-500 ${past ? 'opacity-70' : ''}`}>
      <div className="relative h-[200px] sm:h-full overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center transition-transform duration-[900ms] group-hover:scale-105" style={{ backgroundImage: `url(${e.image || IMAGES.heroA})` }} />
        {d && (
          <div className="absolute top-4 left-4 bg-white rounded-sm px-3 py-2 text-center shadow">
            <p className="font-sans-ah text-[11px] tracking-wide-ah text-[#4a9cd4]">{MON[d.getMonth()]}</p>
            <p className="font-serif-ah text-[28px] leading-none text-[#2f2f2f]">{d.getDate()}</p>
          </div>
        )}
      </div>
      <div className="p-7 flex flex-col">
        <h3 className="font-serif-ah text-[26px] text-[#2f2f2f] leading-snug mb-3 group-hover:text-[#4a9cd4] transition-colors">{e.title}</h3>
        <div className="flex flex-wrap gap-x-6 gap-y-1 font-sans-ah text-[13px] text-[#777] mb-3">
          {fmtTimeRange(e) && <span className="flex items-center gap-1.5"><Clock size={14} /> {fmtTimeRange(e)}</span>}
          {e.location && <span className="flex items-center gap-1.5"><MapPin size={14} /> {e.location}</span>}
          <span className="flex items-center gap-1.5 text-[#4a9cd4]" data-testid={`event-price-${e.id}`}><Tag size={14} /> {priceSummary(e)}</span>
        </div>
        <p className="font-sans-ah text-[14px] text-[#666] font-300 leading-relaxed line-clamp-2 mb-4">{e.description}</p>
        <span className="mt-auto inline-flex items-center gap-2 font-sans-ah text-[12px] tracking-wide-ah text-[#4a9cd4]">
          {past ? 'VIEW EVENT' : 'DETAILS & RSVP'} <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
        </span>
      </div>
    </Link>
  );
}

export default function EventsPage() {
  const [events, setEvents] = useState(null);
  useEffect(() => { getEvents().then(setEvents).catch(() => setEvents([])); }, []);
  const upcoming = (events || []).filter((e) => !isPast(e.date));
  const past = (events || []).filter((e) => isPast(e.date)).reverse();

  return (
    <>
      <PageBanner title="Upcoming Events" crumb="EVENTS" image={IMAGES.heroC}
        subtitle="Networking nights, workshops and community gatherings across the Almaguin Highlands. Save your spot below." />
      <section className="bg-[#f8f6f2] py-16 md:py-20">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10" data-testid="events-list">
          {events === null ? (
            <div className="space-y-6">{['sk-a', 'sk-b'].map((k) => <div key={k} className="h-[220px] bg-white rounded-sm animate-pulse" />)}</div>
          ) : upcoming.length === 0 ? (
            <p data-testid="events-empty" className="text-center font-serif-ah text-[26px] text-[#555] py-16">No upcoming events right now. Check back soon!</p>
          ) : (
            <div className="space-y-6">{upcoming.map((e) => <EventCard key={e.id} e={e} />)}</div>
          )}
          {past.length > 0 && (
            <>
              <h2 className="font-serif-ah text-[30px] text-[#2f2f2f] mt-20 mb-6">Past Events</h2>
              <div className="space-y-6">{past.map((e) => <EventCard key={e.id} e={e} past />)}</div>
            </>
          )}
        </div>
      </section>
      <Footer />
    </>
  );
}
